import React from "react";
import {
  Table, TableBody, TableHead, Box, CircularProgress, Chip, Typography, TableSortLabel
} from "@mui/material";
import { CheckCircle, Block } from "@mui/icons-material";
import {
  StyledTableContainer,
  StyledTableCell,
  StyledTableRow,
} from "../../assets/styles/tableStyle";
import { User } from "./userTypes";
import AppVersionCell from "../../components/AppVersionCell";

const USER_COLUMNS: { key: string; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'firebaseUid', label: 'User UID' },
  { key: 'appType', label: 'AppType' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'appVersion', label: 'App Version' },
  { key: 'currentCity', label: 'Current City' },
  { key: 'role', label: 'Role' },
  { key: 'suspended', label: 'Status' },
  { key: 'phoneVerified', label: 'Phone Verified' },
  { key: 'emailVerified', label: 'Email Verified' },
  { key: 'gender', label: 'Gender' },
];

interface Props {
  rows: User[];
  loading: boolean;
  order: 'asc' | 'desc';
  orderBy: string;
  onSort: (property: string) => void;
  onRowClick: (id: number) => void;
  onContextMenu: (e: React.MouseEvent, row: User) => void;
  /** Live LOCATION_PING_MIN_APP_VERSION, so the version chip can mark probe-eligible builds. */
  appVersionGate?: number;
}

/** The user list table (sortable headers + row rendering). */
const UserTable: React.FC<Props> = ({ rows, loading, order, orderBy, onSort, onRowClick, onContextMenu, appVersionGate = 0 }) => (
    <StyledTableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Table stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <StyledTableRow>
              {USER_COLUMNS.map((col) => (
                  <StyledTableCell key={col.key}>
                    <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => onSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {rows.map((user) => (
                <StyledTableRow
                    key={user.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onContextMenu={e => onContextMenu(e, user)}
                    onClick={() => onRowClick(user.id)}
                >
                  <StyledTableCell>{user.id}</StyledTableCell>
                  <StyledTableCell>{user.firebaseUid}</StyledTableCell>
                  <StyledTableCell>{user.appType}</StyledTableCell>
                  <StyledTableCell>{user.name || "-"}</StyledTableCell>
                  <StyledTableCell>{user.email || "-"}</StyledTableCell>
                  <StyledTableCell>{user.phone}</StyledTableCell>
                  <StyledTableCell>
                    <AppVersionCell
                        driverAppVersion={user.driverAppVersion}
                        riderAppVersion={user.riderAppVersion}
                        role={user.role}
                        lastSeen={user.fcmTokenUpdatedAt}
                        gate={appVersionGate}
                    />
                  </StyledTableCell>
                  <StyledTableCell>{user.currentCity}</StyledTableCell>
                  <StyledTableCell>
                    <Chip label={user.role} color="info" variant="outlined" size="small"/>
                  </StyledTableCell>
                  <StyledTableCell>
                    {user.suspended ? <Chip label="Suspended" color="error" size="small"/> : <Chip label="Active" color="success" size="small"/>}
                  </StyledTableCell>
                  <StyledTableCell>
                    {user.phoneVerified ? <CheckCircle color="success" fontSize="small" /> : <Block color="warning" fontSize="small" />}
                  </StyledTableCell>
                  <StyledTableCell>
                    {user.emailVerified ? <CheckCircle color="success" fontSize="small" /> : <Block color="warning" fontSize="small" />}
                  </StyledTableCell>
                  <StyledTableCell>{user.gender || "-"}</StyledTableCell>
                </StyledTableRow>
            ))}
            {rows.length === 0 && !loading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={USER_COLUMNS.length} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>No users found matching filters.</Typography>
                  </StyledTableCell>
                </StyledTableRow>
            )}
          </TableBody>
        </Table>
      )}
    </StyledTableContainer>
);

export default UserTable;
